import { useState } from "react";
import { ArrowRight, CheckCircle2, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Panel, StatusBadge, Empty } from "@/components/crm/common";
import { ProjectSelect } from "@/components/crm/ProjectSelect";
import { FormDialog } from "@/components/crm/FormDialog";
import { FileBox } from "@/components/crm/Files";
import { useAuth } from "@/context/AuthContext";

const ITEMS = [
  ["presentation", "Final approved presentation"], ["scope", "Confirmed scope and client commitments"], ["designs", "Approved designs, renders and layouts"],
  ["element_sheet", "Final event element sheet"], ["costing", "Approved costing and vendor inputs"], ["milestones", "Event milestones and pending approvals"],
];

export default function Handover() {
  const { items: projects, update } = useCollection("projects");
  const { create: createMsg } = useCollection("messages", null);
  const { can } = useAuth();
  const canEdit = can("Handover", "edit");
  const [pid, setPid] = useState("");
  const [dlg, setDlg] = useState(null);
  const p = projects.find((x) => x.id === pid);
  const stages = ["Approval", "Handover", "Execution"];
  const hc = p?.handover_checklist || {};
  const done = ITEMS.filter(([k]) => hc[k]).length;
  const complete = done === ITEMS.length;

  const setItem = (k, v) => update(p.id, { handover_checklist: { ...hc, [k]: v } }, null);
  const flag = async (f) => !!(await createMsg({ channel: p.name, author: f.author || "Execution Project Lead", text: `Handover missing info: ${f.text}`, kind: "escalation" }, "Flagged to project channel"));

  return (
    <>
      <PageHeader num="06" title="Project Handover" subtitle="A formal checkpoint before confirmed work moves to execution.">
        <ProjectSelect projects={projects} stages={stages} value={pid} onChange={setPid} />
      </PageHeader>
      {!projects.some((x) => stages.includes(x.stage)) && <Panel><Empty text="No projects awaiting handover." /></Panel>}
      {p && (
        <>
          <div className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-4">
            <Panel><div className="text-[11px] uppercase tracking-wider text-slate-400">Prepared by</div><div className="font-display text-lg font-bold">Pitch Team</div><div className="text-sm text-slate-500">CS SPOC: {p.cs_spoc || "—"}</div></Panel>
            <ArrowRight className="mx-auto h-6 w-6 text-zinc-300 rotate-90 md:rotate-0" />
            <Panel><div className="text-[11px] uppercase tracking-wider text-slate-400">Accepted by</div><div className="font-display text-lg font-bold">Execution Team</div><div className="text-sm text-slate-500">Project Lead: {p.lead || "—"}</div></Panel>
          </div>
          <Panel title={`Handover checklist · ${done} of ${ITEMS.length} reviewed`} action={<StatusBadge value={p.stage} />}>
            <div className="h-1.5 rounded-full bg-slate-100 mb-5"><div className="h-1.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${(done / ITEMS.length) * 100}%` }} /></div>
            <div className="divide-y divide-slate-100">
              {ITEMS.map(([k, label]) => (
                <div key={k} className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <label className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
                    <Checkbox data-testid={`handover-check-${k}`} checked={!!hc[k]} disabled={p.stage === "Execution" || !canEdit} onCheckedChange={(v) => setItem(k, !!v)} />
                    {label}
                  </label>
                  <div className="min-w-[160px] max-w-[280px]">
                    <FileBox compact testid={`handover-files-${k}`} entityType="handover" entityId={p.id} category={k} canUpload={canEdit} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              <Button data-testid="handover-flag-button" variant="outline" onClick={() => setDlg({ type: "flag" })}><Flag className="h-4 w-4 mr-1" />Flag Missing Information</Button>
              {p.stage === "Approval" && canEdit && <Button data-testid="handover-start-button" variant="outline" onClick={() => update(p.id, { stage: "Handover" }, "Handover started")}>Start Handover</Button>}
              <Button data-testid="handover-confirm-button" disabled={!complete || p.stage === "Execution" || !can("Handover", "approve")} onClick={() => update(p.id, { stage: "Execution" }, "Handover confirmed → Event Execution")} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="h-4 w-4 mr-1" />{p.stage === "Execution" ? "Handover accepted" : "Confirm Handover"}
              </Button>
            </div>
            {!complete && <p className="text-xs text-slate-500 mt-3">Incomplete handovers remain pending. All 6 items must be reviewed before the Execution Project Lead can accept.</p>}
            {!can("Handover", "approve") && <p className="text-xs text-amber-700 mt-2">Your role can prepare the handover but not accept it.</p>}
          </Panel>
        </>
      )}
      <FormDialog testid="handover-flag-form" open={dlg?.type === "flag"} onOpenChange={(o) => !o && setDlg(null)} title="Flag missing information"
        fields={[{ name: "author", label: "Your name", full: true }, { name: "text", label: "What is missing?", type: "textarea", required: true }]} initial={{}} onSubmit={flag} />
    </>
  );
}
